import ProceduralMesh from "./procedural-mesh";
import GeometryTetrahedron from "./geometries/geo-tetrahedron";
import GeometryOrthohedron from "./geometries/geo-orthohedron";
import GeometryOctahedron from "./geometries/geo-octahedron";
import GeometryDodecahedron from "./geometries/geo-dodecahedron";
import ObjectManager from "../object-manager";
import SlideManager from "../../utils/slide-manager";
import RenderManager from "../../rendering/render-manager";
import GeometryBase from "./geometries/geo-base";
import AssetLibrary from "../../assets/asset-library";

class ProceduralObject extends THREE.Group {
    static Shape_T = Object.freeze({
        TETRAHEDRON: "tetrahedron",
        ORTHOHEDRON: "orthohedron",
        OCTAHEDRON: "octahedron",
        DODECAHEDRON: "dodecahedron"
    });

    static ShapeLabels_T = Object.freeze({
        TETRAHEDRON: "Tetrahedron",
        ORTHOHEDRON: "Cube (orthohedron)",
        OCTAHEDRON: "Octahedron",
        DODECAHEDRON: "Dodecahedron"
    });

    /**
     * @type {GeometryBase}
     */
    _geometries = null;

    constructor(displayType = ProceduralObject.Shape_T.TETRAHEDRON) {
        super();

        this._displayType = displayType;

        this.name = "Platonic Solid";
        this.isProceduralObject = true;
        this.type = "ProceduralObject";
    }

    get displayType() { return this._displayType; }

    setDisplayType(type) {
        switch (this._displayType) {
            case ProceduralObject.Shape_T.TETRAHEDRON:
            case ProceduralObject.Shape_T.ORTHOHEDRON:
            case ProceduralObject.Shape_T.OCTAHEDRON:
            case ProceduralObject.Shape_T.DODECAHEDRON:
                break;
            default:
                throw new Error(`Unknown geometry: ${type}`);
        }

        this._createMesh(type);
        this._displayType = type;
    }

    _disposeOld() {
        this.children.slice().forEach(object => {
            if (!object.isProceduralMesh) return;

            const { uuid } = object;
            const materials = object.material instanceof Array ? object.material : [object.material];

            materials.forEach(material => material.dispose());
            object.geometry.dispose();

            delete Q3.data.assets.objects[uuid];
            delete Q3.assets.objects[uuid];
            SlideManager.slides.forEach(({ animation }) => delete animation[uuid]);

            RenderManager.raycaster.untrack(object);

            object.parent.remove(object);

        });

        while (Q3.data.assets.objects[this.uuid].meshes.pop()) { }
    }

    _forEverySide(callback) {
        for (let i = 0, sides = this._geometries.sides; i < sides; i++) {
            const sideInfo = this._geometries.getSideInfo(i);
            const side = this.children[i];

            callback(side, sideInfo);
        }
    }

    setOffset(distance) {
        this._forEverySide((side, { normal }) => {
            side.position.copy(normal).multiplyScalar(distance);
        });
    }

    set opacity(value) { return this.setOpacity(value); }
    setOpacity(opacity) {
        this._forEverySide(side => {
            side.material.opacity = opacity;
            side.material.transparent = opacity < 1;
        });
    }

    get geometries() { return this._geometries; }

    get color() { return this._geometries.color; }
    set color(color) { this._geometries.setColor(color); }

    get textColor() { return this._geometries.textColor; }
    set textColor(color) { this._geometries.setTextColor(color); }

    get edgeColor() { return this._geometries.edgeColor; }
    set edgeColor(color) { this._geometries.setEdgeColor(color); }

    get fontSize() { return this._geometries.fontSize; }
    set fontSize(fontSize) { this._geometries.setFontSize(fontSize); }

    get font() { return this._geometries.font; }
    set font(font) { this._geometries.setFont(font); }

    createMeshes() {

        let GeometryConstructor;

        switch (this._displayType) {
            case ProceduralObject.Shape_T.TETRAHEDRON: GeometryConstructor = GeometryTetrahedron; break;
            case ProceduralObject.Shape_T.ORTHOHEDRON: GeometryConstructor = GeometryOrthohedron; break;
            case ProceduralObject.Shape_T.OCTAHEDRON: GeometryConstructor = GeometryOctahedron; break;
            case ProceduralObject.Shape_T.DODECAHEDRON: GeometryConstructor = GeometryDodecahedron; break;
            default:
                throw new Error(`Unknown geometry: ${this._displayType}`);
        }

        this._geometries = new GeometryConstructor(1024);

        for (let i = 0, sides = this._geometries.sides; i < sides; i++) {
            const { material, geometry } = this._geometries.getSideInfo(i);
            const mesh = new ProceduralMesh(geometry, material);
            material.isProceduralMeshMaterial = true;

            mesh.onBeforeRender = this.onBeforeRender.bind(mesh);
            mesh.onAfterRender = this.onAfterRender.bind(mesh);

            if (this._displayType === ProceduralObject.Shape_T.TETRAHEDRON && i === 0) {
                mesh.dontShowInHierarchyPanel = true;
            }

            this.add(mesh);
        }
    }

    onBeforeRender() {
        if (RenderManager.mainRenderer.capabilities.isWebGL2 || RenderManager.getExtension("EXT_shader_texture_lod")) return;

        this.__mipMaps = AssetLibrary.envLight.activeMap.mipmaps;
        AssetLibrary.envLight.activeMap.mipmaps = [];
    }

    onAfterRender() {
        if (!this.__mipMaps) return;

        AssetLibrary.envLight.activeMap.mipmaps = this.__mipMaps;
        this.__mipMaps = null;
    }

    _createMesh(displayType) {
        this._disposeOld();

        let GeometryConstructor;

        switch (displayType) {
            case ProceduralObject.Shape_T.TETRAHEDRON: GeometryConstructor = GeometryTetrahedron; break;
            case ProceduralObject.Shape_T.ORTHOHEDRON: GeometryConstructor = GeometryOrthohedron; break;
            case ProceduralObject.Shape_T.OCTAHEDRON: GeometryConstructor = GeometryOctahedron; break;
            case ProceduralObject.Shape_T.DODECAHEDRON: GeometryConstructor = GeometryDodecahedron; break;
            default:
                throw new Error(`Unknown geometry: ${displayType}`);
        }

        const oldTexts = this._geometries
            ? this._displayType === ProceduralObject.Shape_T.TETRAHEDRON
                ? this._geometries.texts.slice(1)
                : this._geometries.texts
            : [];

        const becomesTetrahedron = displayType === ProceduralObject.Shape_T.TETRAHEDRON;
        const textOffset = becomesTetrahedron ? 1 : 0;

        this._geometries = new GeometryConstructor(1024);

        const data = Q3.data.assets.objects[this.uuid];
        const sideList = data.meshes;

        const slideData = SlideManager.currentSlide.animation[this.uuid];
        const hasOffset = slideData && "offset" in slideData;
        const offset = slideData && "offset" in slideData ? slideData.offset : undefined;
        const position = new THREE.Vector3();

        for (let i = 0, sides = this._geometries.sides; i < sides; i++) {
            const hasText = (i - textOffset) in oldTexts;
            const { material, geometry, normal } = this._geometries.getSideInfo(i);
            const name = becomesTetrahedron && i === 0
                ? ""
                : hasText
                    ? becomesTetrahedron
                        ? oldTexts[i - textOffset]
                        : oldTexts[i + textOffset]
                    : `Face '${i + 1 - textOffset}'`;

            if (hasOffset) position.copy(normal).multiplyScalar(offset);
            if (hasText && (i > 0 && becomesTetrahedron || !becomesTetrahedron)) {
                this._geometries.texts[i] = oldTexts[i - textOffset];
                this._geometries._redrawTexts(i);
            }

            const params = {
                geometry,
                material,
                name,
                position,
                text: name
            };

            const { object } = ObjectManager
                .addObject("procedural-mesh", THREE.Math.generateUUID(), this.uuid, data, params);

            if (becomesTetrahedron && i === 0) object.dontShowInHierarchyPanel = true;

            sideList.push(object.uuid);
            this.add(object);
        }
    }

    get sides() { return this._geometries.sides; }
    getText(index) { return this._geometries.getText(index); }
    setText(index, value) {
        this.children[index].name = value;
        return this._geometries.setText(index, value);
    }
}

export default ProceduralObject;
export { ProceduralObject };